// Type definition for items stored in localStorage
export type StorageItem = {
  value: any; // The actual value to be stored
  timestamp: number; // The time when the item was stored
  expiry?: number; // Optional expiry time in milliseconds
};

// Function to save an item to localStorage with an optional expiry time
export const setStorageItem = (key: string, value: any, expiryMs?: number) => {
  try {
    // Create a storage item with the current timestamp and optional expiry
    const item: StorageItem = {
      value,
      timestamp: new Date().getTime(),
      expiry: expiryMs
    };
    // Convert the item to a JSON string and save it in localStorage
    localStorage.setItem(key, JSON.stringify(item));
    return true; // Return true if the operation is successful
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false; // Return false if an error occurs
  }
};

// Function to retrieve an item from localStorage
export const getStorageItem = <T>(key: string): T | null => {
  try {
    // Get the item string from localStorage
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null; // Return null if the item does not exist

    // Parse the item string to a StorageItem object
    const item: StorageItem = JSON.parse(itemStr);
    
    // Check if the item has expired
    if (item.expiry && new Date().getTime() > item.timestamp + item.expiry) {
      localStorage.removeItem(key); // Remove the item if it has expired
      return null; // Return null if the item has expired
    }

    return item.value as T; // Return the stored value
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null; // Return null if an error occurs
  }
};

// Function to remove an item from localStorage
export const removeStorageItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key); // Remove the item from localStorage
    return true; // Return true if the operation is successful
  } catch (error) {
    console.error('Error removing from localStorage:', error);
    return false; // Return false if an error occurs
  }
};

// Function to clear all items from localStorage
export const clearStorage = (): boolean => {
  try {
    localStorage.clear(); // Clear all items from localStorage
    return true; // Return true if the operation is successful
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false; // Return false if an error occurs
  }
}; 